import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { Field, Form, FieldProps, ErrorMessage, useFormikContext } from 'formik';
import { FormField, Select, Label } from '@round/ui-kit';
import { GenericDropdownOption } from 'App.types';
import { ValueType } from 'react-select';
import useNonNullContext from 'Hooks/useNonNullContext';
import styles from './PostPaymentRequestForm.module.css';
import cn from 'classnames';
import usePayeeSelect, { fetchOptions } from 'Modules/Finance/hooks/usePayeeSelect';
import { BasePostPaymentRequestData, PaymentMethod, PaypalPaymentBody, TipaltiPaymentBody } from '@round/api';
import DatePicker from 'ui/DataEntry/DatePicker/DatePicker';
import { getThirtyDaysDateStringValue } from '../helpers';
import { useCallback } from 'react';

export type ScheduledDate = '30days' | 'immediate' | 'custom';

type TipaltiFields = Partial<Pick<TipaltiPaymentBody, 'tipalti_payee_id' | 'tipalti_scheduled_datetime'>> & {
    scheduledDate: ScheduledDate;
};

export type PaymentRequestFormValues = Omit<BasePostPaymentRequestData<PaymentMethod>, 'currency'> & {
    currency: ValueType<GenericDropdownOption<number>, false>;
} & TipaltiFields &
    PaypalPaymentBody;

const PostPaymentRequestForm = () => {
    const { values, setFieldValue, errors, setErrors, validateForm } = useFormikContext<PaymentRequestFormValues>();

    const { currencies } = useNonNullContext(OptionsContext);
    const currencyOptions = currencies.map((c) => ({
        value: c.id,
        label: c.name,
    }));

    const handlePaymentMethodChange = (value: PaymentRequestFormValues['payment_method']) => {
        if (values.payment_method === value) {
            return;
        }
        setErrors({
            ...errors,
            paypal_email_address: '',
            tipalti_payee_id: '',
        });

        setFieldValue('payment_method', value);
        // since the payment method field value won't update until next render, we'll revalidate the form manually
        // to ensure the validation schema is applied correctly
        validateForm({ ...values, payment_method: value }).then((formErrors) => {
            setErrors(formErrors);
        });
    };

    const handleScheduledDateChange = (
        dateType: PaymentRequestFormValues['scheduledDate'],
        date: string | undefined
    ) => {
        if (values.scheduledDate === dateType) {
            return;
        }

        setErrors({
            ...errors,
            scheduledDate: '',
            tipalti_scheduled_datetime: '',
        });

        setFieldValue('scheduledDate', dateType);
        setFieldValue('tipalti_scheduled_datetime', date);

        // since the scheduled date field value won't update until next render, we'll revalidate the form manually
        // to ensure the validation schema is applied correctly
        validateForm({ ...values, scheduledDate: dateType, tipalti_scheduled_datetime: date }).then((formErrors) => {
            setErrors(formErrors);
        });
    };

    const { props: payeeSelectProps } = usePayeeSelect({
        initOn: 'menuOpen',
        fetchOptions: useCallback<typeof fetchOptions>((params, requestInit) => {
            return fetchOptions({ ...params, is_payable: true }, requestInit);
        }, []),
    });

    return (
        <Form className={styles.form}>
            <div className={styles.splitSection}>
                <FormField>
                    <Label htmlFor="currency">Currency</Label>
                    <Field id="currency" name="currency">
                        {(fieldProps: FieldProps<PaymentRequestFormValues['currency']>) => (
                            <Select
                                options={currencyOptions}
                                placeholder="Select currency"
                                value={fieldProps.field.value}
                                onChange={(value) => {
                                    fieldProps.form.setFieldValue(fieldProps.field.name, value);
                                }}
                            />
                        )}
                    </Field>
                    <ErrorMessage name="currency">{(msg) => <span className={styles.error}>{msg}</span>}</ErrorMessage>
                </FormField>

                <FormField>
                    <Label htmlFor="amount">Amount</Label>
                    <Field
                        type="number"
                        id="amount"
                        name="amount"
                        placeholder="Enter amount"
                        decimalsLimit={2}
                        className={styles.currencyInput}
                    />
                    <ErrorMessage name="amount">{(msg) => <span className={styles.error}>{msg}</span>}</ErrorMessage>
                </FormField>
            </div>

            <FormField>
                <Label htmlFor="paymentMethod">Payment Method</Label>
                <fieldset id="paymentMethod" className={cn(styles.splitSection, styles.radioOptions)}>
                    <label htmlFor="paypalRadio" className={styles.radioButton}>
                        <input
                            type="radio"
                            id="paypalRadio"
                            name="payment_method"
                            value="paypal"
                            checked={values.payment_method === 'paypal'}
                            onChange={() => handlePaymentMethodChange('paypal')}
                        />
                        <span>PayPal</span>
                    </label>
                    <label htmlFor="tipaltiRadio" className={styles.radioButton}>
                        <input
                            type="radio"
                            id="tipaltiRadio"
                            name="payment_method"
                            value="tipalti"
                            checked={values.payment_method === 'tipalti'}
                            onChange={() => handlePaymentMethodChange('tipalti')}
                        />
                        <span>Tipalti</span>
                    </label>
                </fieldset>

                <ErrorMessage name="payment_method">{(msg) => <div className={styles.error}>{msg}</div>}</ErrorMessage>
            </FormField>

            {values.payment_method === 'paypal' ? (
                <FormField>
                    <Label htmlFor="paypal_email_address">PayPal Email</Label>
                    <Field
                        type="email"
                        id="paypal_email_address"
                        name="paypal_email_address"
                        placeholder="Enter PayPal email address"
                    />
                    <ErrorMessage name="paypal_email_address">
                        {(msg) => <span className={styles.error}>{msg}</span>}
                    </ErrorMessage>
                </FormField>
            ) : values.payment_method === 'tipalti' ? (
                <>
                    <FormField>
                        <Label htmlFor="tipalti_payee_id">Payee</Label>
                        <Field id="tipalti_payee_id" name="tipalti_payee_id">
                            {(fieldProps: FieldProps<PaymentRequestFormValues['tipalti_payee_id']>) => (
                                <Select
                                    {...payeeSelectProps}
                                    onChange={(opt, action) => {
                                        payeeSelectProps.onChange?.(opt, action);
                                        fieldProps.form.setFieldValue(fieldProps.field.name, opt?.id);
                                    }}
                                    onBlur={(e) => {
                                        fieldProps.field.onBlur(e);
                                        fieldProps.form.setFieldTouched(fieldProps.field.name, true);
                                    }}
                                    placeholder="Select payee"
                                    formatOptionLabel={(option, meta) => {
                                        const extraInfo = [option.payment_method_currency, option.email]
                                            .filter((v): v is string => !!v)
                                            .join(' | ');

                                        if (meta.context === 'value') {
                                            return (
                                                <span>
                                                    {option.label}
                                                    {extraInfo.length ? ` | ${extraInfo}` : ''}
                                                </span>
                                            );
                                        }

                                        return (
                                            <div>
                                                <strong className={styles.tipaltiOptionLabel}>{option.label}</strong>
                                                {extraInfo.length ? (
                                                    <>
                                                        <br />
                                                        <span>{extraInfo}</span>
                                                    </>
                                                ) : null}
                                            </div>
                                        );
                                    }}
                                />
                            )}
                        </Field>
                        <ErrorMessage name="tipalti_payee_id">
                            {(msg) => <span className={styles.error}>{msg}</span>}
                        </ErrorMessage>
                    </FormField>

                    <FormField>
                        <Label htmlFor="scheduleDate">Scheduled Date</Label>
                        <fieldset id="scheduleDate" className={cn(styles.splitSection, styles.radioOptions)}>
                            <label htmlFor="30daysRadio" className={styles.radioButton}>
                                <input
                                    type="radio"
                                    id="30daysRadio"
                                    name="scheduleDate"
                                    value="30days"
                                    checked={values.scheduledDate === '30days'}
                                    onChange={() => handleScheduledDateChange('30days', getThirtyDaysDateStringValue())}
                                />
                                <div>30 Days</div>
                                <div className={styles.subText}>{`(${new Date(
                                    getThirtyDaysDateStringValue()
                                ).toLocaleDateString(undefined, {
                                    day: '2-digit',
                                    month: 'short',
                                    year: '2-digit',
                                })})`}</div>
                            </label>

                            <label htmlFor="immediateRadio" className={styles.radioButton}>
                                <input
                                    type="radio"
                                    id="immediateRadio"
                                    name="scheduleDate"
                                    value="immediate"
                                    checked={values.scheduledDate === 'immediate'}
                                    onChange={() => handleScheduledDateChange('immediate', undefined)}
                                />
                                <div>On Approval</div>
                                <div className={styles.subText}>(ASAP)</div>
                            </label>

                            <label htmlFor="customDateRadio" className={styles.radioButton}>
                                <input
                                    type="radio"
                                    id="customDateRadio"
                                    name="scheduleDate"
                                    value="custom"
                                    checked={values.scheduledDate === 'custom'}
                                    onChange={() => handleScheduledDateChange('custom', new Date().toISOString())}
                                />
                                {values.scheduledDate === 'custom' ? (
                                    <DatePicker
                                        className={styles.datePickerSelect}
                                        value={values.tipalti_scheduled_datetime || ''}
                                        onChange={(date) => {
                                            setFieldValue('tipalti_scheduled_datetime', date, true);
                                        }}
                                        startOpen
                                        dateTimeFormatOptions={{
                                            timeZone: 'Europe/London',
                                            day: '2-digit',
                                            month: 'short',
                                            year: '2-digit',
                                        }}
                                        minDate={new Date()}
                                    />
                                ) : (
                                    <span>Custom</span>
                                )}
                            </label>
                        </fieldset>

                        <ErrorMessage name="scheduledDate">
                            {(msg) => <span className={styles.error}>{msg}</span>}
                        </ErrorMessage>
                        <ErrorMessage name="customDate">
                            {(msg) => <span className={styles.error}>{msg}</span>}
                        </ErrorMessage>
                    </FormField>
                </>
            ) : null}
        </Form>
    );
};

export default PostPaymentRequestForm;
